/**
 * CallSession — pure-logic state machine for a call lifecycle.
 *
 * No media. No libp2p. No WebRTC. The state machine is driven by `apply(event)`
 * from a CallManager that owns the actual side-effects. The contract is:
 *
 *   - States from voice-video-design.md § Decision 6.
 *   - Every transition is logged on the in-memory `history` array; the manager
 *     turns these into audit_log entries.
 *   - When the call ends (any terminal state), the manager writes a single
 *     call_history row with `disposition` derived from `toDisposition()`.
 *   - Reconnection policy: up to 3 ICE restart attempts before giving up.
 *
 * This file is browser-/node-agnostic; tests exercise it directly.
 */

export type CallState =
  | 'idle'
  | 'ringing'
  | 'connecting'
  | 'connected'
  | 'interrupted'
  | 'reconnecting'
  | 'ending'
  | 'ended'
  | 'declined'
  | 'failed';

export type CallEvent =
  | 'ring'              // begin: outgoing ring sent OR incoming ring received
  | 'accept'            // user accepted an incoming ring (or remote accepted ours)
  | 'decline'           // user declined an incoming ring
  | 'media-flowing'     // WebRTC ICE established + tracks negotiated
  | 'signaling-failed'  // SDP/ICE exchange aborted before media
  | 'ice-disconnect'    // ICE went disconnected/failed mid-call
  | 'ice-restart'       // CallManager kicked off an ICE restart
  | 'reconnect-failed'  // an ICE restart attempt didn't recover
  | 'end'               // local user clicked end
  | 'bye-sent'          // outgoing bye envelope confirmed; tearing down
  | 'timeout'           // ring expired without accept
  | 'cancel';           // outgoing ring cancelled before remote accept

// Re-export the canonical schema type for callers.
export type { CallDisposition } from '../storage/schema';
import type { CallDisposition } from '../storage/schema';

export interface CallContext {
  callId: string;
  threadId: string;
  direction: 'incoming' | 'outgoing';
  kind: 'audio' | 'video';
  /** Peer pubkey hex; for outgoing this is whom we're calling, for incoming it's the caller. */
  peerPubkey: string;
  startedAt: string;
  acceptedAt?: string;
  endedAt?: string;
  reconnectAttempts: number;
}

export interface TransitionRecord {
  from: CallState;
  to: CallState;
  via: CallEvent;
  at: number;  // performance.now() at the moment of transition
}

/** Maximum ICE restart attempts before we give up. Per design doc. */
export const MAX_RECONNECT_ATTEMPTS = 3;

/** Transition table. Pure data — easy to inspect, easy to test. */
const TRANSITIONS: Partial<Record<CallState, Partial<Record<CallEvent, CallState>>>> = {
  idle: {
    ring: 'ringing',
  },
  ringing: {
    accept: 'connecting',
    decline: 'declined',
    timeout: 'ended',
    cancel: 'ended',
    'signaling-failed': 'failed',
  },
  connecting: {
    'media-flowing': 'connected',
    'signaling-failed': 'failed',
    end: 'ending',
    cancel: 'ended',
  },
  connected: {
    'ice-disconnect': 'interrupted',
    end: 'ending',
  },
  interrupted: {
    'ice-restart': 'reconnecting',
    end: 'ending',
    'reconnect-failed': 'ended',  // out of retries; manager calls this directly
  },
  reconnecting: {
    'media-flowing': 'connected',
    'reconnect-failed': 'interrupted',   // try again until attempts exhausted
    end: 'ending',
  },
  ending: {
    'bye-sent': 'ended',
    'signaling-failed': 'ended',  // best-effort bye — terminal regardless
  },
  // terminal states have no outgoing transitions
  ended: {},
  declined: {},
  failed: {},
};

export const TERMINAL_STATES: ReadonlySet<CallState> = new Set([
  'ended', 'declined', 'failed',
]);

export interface ApplyResult {
  ok: boolean;
  from: CallState;
  to: CallState;
  /** Set when ok=false: the rejection reason. */
  reason?: string;
}

export class CallSession {
  #state: CallState;
  #ctx: CallContext;
  #history: TransitionRecord[] = [];

  constructor(ctx: CallContext, initialState: CallState = 'idle') {
    this.#ctx = { ...ctx, reconnectAttempts: ctx.reconnectAttempts ?? 0 };
    this.#state = initialState;
  }

  get state(): CallState { return this.#state; }
  get context(): CallContext { return { ...this.#ctx }; }
  get history(): readonly TransitionRecord[] { return this.#history; }
  isTerminal(): boolean { return TERMINAL_STATES.has(this.#state); }

  /** Apply an event. Returns ok=false (with a reason) if the transition is illegal. */
  apply(event: CallEvent): ApplyResult {
    const from = this.#state;

    // Special-case reconnect-failed handling: on `interrupted` → if we've used
    // up our attempts, the legal target is `ended`; if not, attempts increment
    // and we stay in `interrupted` waiting for the manager to fire ice-restart.
    if (event === 'reconnect-failed' && from === 'reconnecting') {
      this.#ctx.reconnectAttempts += 1;
      if (this.#ctx.reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
        return this.#transition('ended', event);
      }
      return this.#transition('interrupted', event);
    }

    const target = TRANSITIONS[from]?.[event];
    if (target === undefined) {
      return {
        ok: false,
        from,
        to: from,
        reason: `illegal transition: ${from} --(${event})--> ?`,
      };
    }
    return this.#transition(target, event);
  }

  /** Helper for tests + debugging: try a transition without applying it. */
  canApply(event: CallEvent): boolean {
    if (event === 'reconnect-failed' && this.#state === 'reconnecting') return true;
    return TRANSITIONS[this.#state]?.[event] !== undefined;
  }

  /** Map the terminal state + history into a call_history disposition. */
  toDisposition(): CallDisposition {
    if (!this.isTerminal()) {
      throw new Error(`CallSession.toDisposition: not in terminal state (current: ${this.#state})`);
    }
    if (this.#state === 'declined') return 'declined';
    if (this.#state === 'failed') return 'failed';

    // `ended` covers several roads; look at the path.
    const lastEvent = this.#history[this.#history.length - 1]?.via;
    if (lastEvent === 'timeout' && this.#ctx.direction === 'incoming') return 'missed';
    if (lastEvent === 'cancel' || lastEvent === 'timeout') return 'cancelled';
    if (lastEvent === 'reconnect-failed') return 'failed';
    // Normal "ended via bye-sent after connected" path.
    return 'completed';
  }

  /** Used by the manager when finalizing the call_history row. */
  setEndedAt(iso: string): void { this.#ctx.endedAt = iso; }
  setAcceptedAt(iso: string): void { this.#ctx.acceptedAt = iso; }

  #transition(to: CallState, via: CallEvent): ApplyResult {
    const from = this.#state;
    this.#state = to;
    this.#history.push({ from, to, via, at: performanceNow() });
    return { ok: true, from, to };
  }
}

function performanceNow(): number {
  // Browser + node both expose `performance.now()` since Node 16+.
  // Falls through to Date.now() in the unusual case it's missing.
  if (typeof performance !== 'undefined' && typeof performance.now === 'function') {
    return performance.now();
  }
  return Date.now();
}
